<template>
  <div>
    <el-dialog
      :title="title"
      :visible.sync="isVisible"
      width="40%"
      :before-close="handleClose"
    >
      <div>
        <el-table :data="document_tags" border style="width: 100%" :row-class-name="getRowClassName">
          <el-table-column label="Enable" align="center" width="100px">
            <template slot-scope="scope">
              <el-checkbox
                :checked="isSelectedTagContains(scope.row.name)"
                @change="handleTagChange(scope.row.name, $event)"
              ></el-checkbox>
            </template>
          </el-table-column>
          <el-table-column prop="name" label="Category">
            <template slot-scope="scope">
              <span>{{ scope.row.name }}</span>
              <span v-if="hasNoTagsSelected(scope.row.name)" class="warning-text">
                <i class="el-icon-warning" style="color: #E6A23C; margin-left: 8px;"></i>
                <span style="color: #E6A23C; font-size: 12px; margin-left: 4px;">No tags selected</span>
              </span>
            </template>
          </el-table-column>
          <el-table-column label="Tags">
            <template slot-scope="scope">
              <div style="display: inline-block; width: 100%">
                <el-select
                  placeholder="Select tags..."
                  multiple=""
                  v-model="selectedTags[scope.row.name]"
                  style="width: 100%"
                  :disabled="!isSelectedTagContains(scope.row.name)"
                >
                  <el-option
                    v-for="(tag, index) in scope.row.tags"
                    :key="index"
                    :label="tag"
                    :value="tag"
                  ></el-option>
                </el-select>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div v-if="hasAnyWarnings" class="warning-note">
        <i class="el-icon-info"></i>
        <div class="warning-note-text">
          <strong>Warning:</strong> Categories marked with a warning icon have no tags selected. 
          These categories will be removed when you confirm your changes.
        </div>
      </div>
      <div style="margin-top: 30px; font-size: 16px">
        <span>{{ noteText }}</span>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="handleClose">Cancel</el-button>
        <el-button type="primary" @click="handleConfirm" :loading="isLoading">Confirm</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import _ from "lodash";
import { v4 as uuid } from "uuid";

export default {
  props: [
    "shouldShowTagEditor", 
    "tagEditorVisible",
    "selectedItem", 
    "isLoading", 
    "itemType", 
    "title", 
    "noteText"
  ],
  data() {
    return {
      selectedTags: {},
      newTags: [],
      tempSelectedTags: {},
      tempNewItem: {},
    };
  },
  methods: {
    handleClose() {
      this.$confirm("Are you sure you want to close this window", "warning", {
        confirmButtonText: "OK",
        cancelButtonText: "Cancel",
        type: "info",
      }).then(() => {
        if (this.itemType === 'document') {
          this.$emit("updateTagEditorVisible", false);
        } else {
          this.$emit("handleCloseTagEditor");
        }
      });
    },
    isSelectedTagContains(tagName) {
      return _.has(this.tempSelectedTags, tagName);
    },
    hasNoTagsSelected(categoryName) {
      return this.isSelectedTagContains(categoryName) && 
             (!this.selectedTags[categoryName] || this.selectedTags[categoryName].length === 0);
    },
    getRowClassName({ row }) {
      if (this.hasNoTagsSelected(row.name)) {
        return 'warning-row';
      }
      return '';
    },
    handleTagChange(itemName, value) {
      if (value) {
        this.$set(this.selectedTags, itemName, []);
        this.$set(this.tempSelectedTags, itemName, []);
      } else {
        this.$delete(this.selectedTags, itemName);
        this.$delete(this.tempSelectedTags, itemName, []);
      }
    },
    async handleConfirm() {
      const categoriesWithoutTags = [];
      
      // Check for enabled categories without tags
      Object.keys(this.tempSelectedTags).forEach((categoryName) => {
        if (!this.selectedTags[categoryName] || this.selectedTags[categoryName].length === 0) {
          categoriesWithoutTags.push(categoryName);
        }
      });

      // Show confirmation dialog if there are categories without tags
      if (categoriesWithoutTags.length > 0) {
        const categoryList = categoriesWithoutTags.join(', ');
        const message = `The following categories are enabled but have no tags selected and will be removed: (${categoryList}). Do you want to continue?`;
        
        try {
          await this.$confirm(message, 'Categories without tags will be removed', {
            confirmButtonText: 'Continue',
            cancelButtonText: 'Cancel',
            type: 'warning',
          });
        } catch {
          return; // User cancelled
        }
      }

      const newItem = _.cloneDeep(this.selectedItem);
      const tags = {};
      Object.keys(this.selectedTags).forEach((key) => {
        if (this.selectedTags[key].length > 0) {
          tags[key] = this.selectedTags[key];
        }
      });

      if (this.itemType === 'article' || this.itemType === 'document') {
        newItem.isUpload = false;
        newItem.isUploaded = false;
      }
      
      newItem.tags = tags;
      newItem.uploadedBy = this.$store.state.profile?.email || null;

      if (this.itemType === 'document') {
        this.$emit("handleUpdateDocumentTag", { newDocument: newItem });
      } else {
        this.$emit("handleUpdateItemTag", { 
          newItem, 
          oldItem: this.selectedItem,
          itemType: this.itemType 
        });
      }
    },
  },
  computed: {
    isVisible: {
      get() {
        return this.shouldShowTagEditor || this.tagEditorVisible;
      },
      set(value) {
        if (this.itemType === 'document') {
          this.$emit("updateTagEditorVisible", value);
        } else {
          this.$emit("handleCloseTagEditor");
        }
      }
    },
    modules: {
      get() {
        return this.$store.state.modules;
      },
      set(value) {
        this.$store.state.modules = value;
      },
    },
    genai_settings() {
      return this.modules.genai;
    },
    document_tags() {
      return this.genai_settings?.knowledge_document.tags || [];
    },
    hasAnyWarnings() {
      return Object.keys(this.tempSelectedTags).some(categoryName => 
        this.hasNoTagsSelected(categoryName)
      );
    },
  },
  watch: {
    "selectedItem.tags": {
      handler(newTags) {
        if (newTags) {
          this.selectedTags = _.cloneDeep(newTags);
          this.tempSelectedTags = _.cloneDeep(newTags);
        }
      },
      immediate: true,
    },
  },
  mounted() {
    if (this.selectedItem.tags) {
      this.selectedTags = _.cloneDeep(this.selectedItem.tags);
      this.tempSelectedTags = _.cloneDeep(this.selectedItem.tags);
    }
  },
};
</script>

<style lang="scss" scoped>
::v-deep .warning-row {
  background-color: #fef0e8 !important;
}

::v-deep .warning-row:hover {
  background-color: #fce4d3 !important;
}

.warning-text {
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
}

.warning-text .el-icon-warning {
  color: #E6A23C;
  margin-right: 4px;
}

.warning-text span {
  color: #E6A23C;
  font-size: 12px;
  font-weight: 500;
}

// Add a general warning note at the bottom
.warning-note {
  background-color: #fef0e8;
  border: 1px solid #f5d19e;
  border-radius: 4px;
  padding: 12px;
  margin-top: 10px;
  display: flex;
  align-items: flex-start;
  
  .el-icon-info {
    color: #E6A23C;
    margin-right: 8px;
    margin-top: 2px;
  }
  
  .warning-note-text {
    color: #6b4423;
    font-size: 13px;
    line-height: 1.4;
  }
}
</style>